/**
 * Iter60.p55a — Incident / Emergency Report module (module-scoped view).
 *
 * Renders on `/m/{ic,cn,ap}/incidents`. Filers = Manager (module-scoped),
 * HR (both roles), Admin. Attendance Officer 403s at the API layer.
 *
 * Layout:
 *   - Filter bar (status / severity / type / red-flag-only) + create button
 *   - Table with red-flag left border + crimson chip column
 *   - Sheet drawer for detail view + attachment upload/download + status change
 *   - Dialog for the create form (all fields on one screen; scrollable)
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import useFilterUrlSync from "../hooks/useFilterUrlSync";
import { toast } from "sonner";

import { api, formatApiError, API_BASE } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ROLES } from "../lib/rbac";

import ModuleIdChip from "../components/ModuleIdChip";
import { Button } from "../components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../components/ui/sheet";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";

// --- constants --------------------------------------------------------------
export const TYPE_LABELS = {
    injury: "Injury",
    property_damage: "Property damage",
    theft_loss: "Theft / loss",
    violence_harassment: "Violence / harassment",
    safety_violation: "Safety violation",
    emergency_medical: "Emergency — medical",
    emergency_fire: "Emergency — fire",
    emergency_security: "Emergency — security",
    near_miss: "Near miss",
    other: "Other",
};
export const SEVERITIES = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "critical", label: "Critical" },
];
export const STATUSES = [
    { value: "open", label: "Open" },
    { value: "under_investigation", label: "Under investigation" },
    { value: "resolved", label: "Resolved" },
    { value: "closed", label: "Closed" },
];
export const SEVERITY_TONE = {
    low: { bg: "rgba(0,0,0,0.06)", fg: "#3a3a3a" },
    medium: { bg: "rgba(228, 155, 45, 0.12)", fg: "#a56a15" },
    high: { bg: "rgba(210, 100, 46, 0.14)", fg: "#8f3d16" },
    critical: { bg: "rgba(168, 50, 74, 0.14)", fg: "var(--asl-crimson, #A8324A)" },
};
export const STATUS_TONE = {
    open: { bg: "rgba(27, 42, 86, 0.10)", fg: "var(--asl-navy, #1B2A56)" },
    under_investigation: { bg: "rgba(228, 155, 45, 0.14)", fg: "#a56a15" },
    resolved: { bg: "rgba(46, 128, 79, 0.14)", fg: "#2e804f" },
    closed: { bg: "rgba(0,0,0,0.06)", fg: "#5a5a5a" },
};

// --- helpers ----------------------------------------------------------------
export function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}
function fmtBytes(n) {
    if (!n && n !== 0) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
function canManage(user) {
    if (!user) return false;
    return [ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_HR_OFFICER].includes(user.role);
}

// --- page -------------------------------------------------------------------
export default function Incidents() {
    const { moduleCode } = useParams();
    const codeUpper = (moduleCode || "").toUpperCase();
    const { user } = useAuth();

    const [moduleId, setModuleId] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        status: "any",
        severity: "any",
        incident_type: "any",
        red_flag_only: false,
    });
    // Iter70 — Two-way URL sync for the Incidents filter bar. Status,
    // severity, incident_type persist; the red-flag toggle joins them too.
    // Debounced 300ms to keep history entries clean.
    const INCIDENT_DEFAULTS = { status: "any", severity: "any", incident_type: "any", red_flag_only: false };
    useFilterUrlSync({
        filters,
        defaults: INCIDENT_DEFAULTS,
        paramMap: {
            status: "status",
            severity: "severity",
            incident_type: "type",
            red_flag_only: "red_flag",
        },
        hydrate: (patch) => setFilters((f) => ({ ...f, ...patch })),
    });
    const clearAllFilters = useCallback(() => setFilters(INCIDENT_DEFAULTS), []);
    const [createOpen, setCreateOpen] = useState(false);
    const [detailId, setDetailId] = useState(null);

    // Iter60.p60 Part A — Admin-only bulk & individual delete state.
    //   `selectedIds` is a Set for O(1) toggle checks. `confirmDelete`
    //   holds either { kind: "bulk", ids: [...] } or { kind: "single",
    //   id, label } — a single dialog handles both modes.
    const isAdmin = user?.role === ROLES.KAAFI_ADMIN;
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
    const toggleSelect = useCallback((id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);
    const toggleSelectAll = useCallback((all) => {
        setSelectedIds(() => {
            if (!all) return new Set();
            return new Set(items.map((i) => i.id));
        });
    }, [items]);

    // Iter60.p55b — `?open={id}` deep-link support so the discipline
    // reverse-cross-link + KPI tile can jump straight into a row's
    // detail drawer. Also honour the `?status=open` filter param.
    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        const openId = searchParams.get("open");
        if (openId) {
            setDetailId(openId);
            const next = new URLSearchParams(searchParams);
            next.delete("open");
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, setSearchParams]);
    useEffect(() => {
        const statusParam = searchParams.get("status");
        if (statusParam) setFilters((f) => ({ ...f, status: statusParam }));
        // Only run once per landing; drops the param after applying.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Resolve moduleId once via /modules — same pattern other module pages use.
    useEffect(() => {
        (async () => {
            try {
                const res = await api.get("/modules");
                const list = res.data?.items || res.data || [];
                const found = list.find(
                    (m) => (m.code || "").toUpperCase() === codeUpper,
                );
                if (found) setModuleId(found.id);
            } catch (e) {
                toast.error(formatApiError(e));
            }
        })();
    }, [codeUpper]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { module_code: codeUpper, page_size: 200 };
            if (filters.status !== "any") params.status = filters.status;
            if (filters.severity !== "any") params.severity = filters.severity;
            if (filters.incident_type !== "any") params.incident_type = filters.incident_type;
            if (filters.red_flag_only) params.red_flag_only = true;
            const res = await api.get("/incidents", { params });
            setItems(res.data?.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [codeUpper, filters]);

    useEffect(() => {
        load();
    }, [load]);

    // Iter60.p60 Part A — drop selection whenever the row set changes.
    useEffect(() => {
        setSelectedIds((prev) => {
            const visible = new Set(items.map((i) => i.id));
            let changed = false;
            const next = new Set();
            prev.forEach((id) => {
                if (visible.has(id)) next.add(id);
                else changed = true;
            });
            return changed ? next : prev;
        });
    }, [items]);

    const runDelete = useCallback(async () => {
        if (!confirmDelete) return;
        setDeleting(true);
        try {
            if (confirmDelete.kind === "bulk") {
                const res = await api.post("/incidents/bulk-delete", { ids: confirmDelete.ids });
                const { deleted_count = 0, attachments_deleted = 0, missing_ids = [] } = res.data || {};
                toast.success(
                    `Deleted ${deleted_count} incident${deleted_count === 1 ? "" : "s"}` +
                    (attachments_deleted ? ` and ${attachments_deleted} attachment${attachments_deleted === 1 ? "" : "s"}` : "") +
                    (missing_ids.length ? ` — ${missing_ids.length} already gone` : "") + ".",
                );
            } else {
                await api.delete(`/incidents/${confirmDelete.id}`);
                toast.success("Incident deleted.");
            }
            setConfirmDelete(null);
            clearSelection();
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setDeleting(false);
        }
    }, [confirmDelete, load, clearSelection]);

    const canFile = user && [
        ROLES.KAAFI_ADMIN, ROLES.KAAFI_HR, ROLES.ASL_HR_OFFICER, ROLES.ASL_DEPT_MGR,
    ].includes(user.role);

    return (
        <div className="space-y-6" data-testid="incidents-page">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <p className="text-sm asl-text-2">
                        {codeUpper} module — file, review, and resolve workplace incidents and emergencies.
                    </p>
                </div>
                {canFile && (
                    <Button
                        data-testid="incidents-create-btn"
                        onClick={() => setCreateOpen(true)}
                        className="asl-btn-primary"
                    >
                        + New incident
                    </Button>
                )}
            </div>

            <FilterBar filters={filters} setFilters={setFilters} onClear={clearAllFilters} />

            {isAdmin && selectedIds.size > 0 && (
                <div
                    className="asl-card p-3 flex items-center justify-between gap-3"
                    style={{
                        borderLeft: "3px solid var(--asl-crimson, #A8324A)",
                    }}
                    data-testid="incidents-bulk-toolbar"
                >
                    <div className="text-sm asl-text-1">
                        <span
                            className="font-semibold asl-navy-text"
                            data-testid="incidents-bulk-count"
                        >{selectedIds.size}</span>
                        {" "}selected
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={clearSelection}
                            data-testid="incidents-bulk-clear"
                        >
                            Clear
                        </Button>
                        <Button
                            size="sm"
                            className="bg-[color:var(--asl-crimson,#A8324A)] text-white hover:opacity-90"
                            onClick={() => setConfirmDelete({
                                kind: "bulk",
                                ids: Array.from(selectedIds),
                            })}
                            data-testid="incidents-bulk-delete-btn"
                        >
                            Delete selected
                        </Button>
                    </div>
                </div>
            )}

            <div className="asl-card">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="incidents-table">
                        <thead>
                            <tr className="text-left asl-thead">
                                {isAdmin && (
                                    <th className="px-4 py-3 w-10">
                                        <Checkbox
                                            checked={items.length > 0 && selectedIds.size === items.length}
                                            onCheckedChange={(v) => toggleSelectAll(!!v)}
                                            data-testid="incidents-select-all"
                                            aria-label="Select all incidents"
                                        />
                                    </th>
                                )}
                                <th className="px-4 py-3 w-40">Date</th>
                                <th className="px-4 py-3 w-24">Red flag</th>
                                <th className="px-4 py-3">Type</th>
                                <th className="px-4 py-3 w-28">Severity</th>
                                <th className="px-4 py-3">Employees</th>
                                <th className="px-4 py-3">Location</th>
                                <th className="px-4 py-3 w-40">Status</th>
                                <th className="px-4 py-3 w-40">Reporter</th>
                                {isAdmin && <th className="px-4 py-3 w-16 text-right">Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={isAdmin ? 10 : 8} className="px-4 py-6 asl-text-2 text-center">
                                        Loading incidents…
                                    </td>
                                </tr>
                            )}
                            {!loading && items.length === 0 && (
                                <tr>
                                    <td colSpan={isAdmin ? 10 : 8} className="px-4 py-6 asl-text-2 text-center">
                                        No incidents match the current filters.
                                    </td>
                                </tr>
                            )}
                            {!loading && items.map((it) => (
                                <IncidentRow
                                    key={it.id}
                                    it={it}
                                    onOpen={() => setDetailId(it.id)}
                                    isAdmin={isAdmin}
                                    selected={selectedIds.has(it.id)}
                                    onToggleSelect={() => toggleSelect(it.id)}
                                    onDeleteRow={() => setConfirmDelete({
                                        kind: "single",
                                        id: it.id,
                                        label: TYPE_LABELS[it.incident_type] || it.incident_type,
                                    })}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {isAdmin && (
                <Dialog
                    open={!!confirmDelete}
                    onOpenChange={(v) => !v && !deleting && setConfirmDelete(null)}
                >
                    <DialogContent data-testid="incidents-delete-dialog">
                        <DialogHeader>
                            <DialogTitle>
                                {confirmDelete?.kind === "bulk"
                                    ? `Delete ${confirmDelete.ids?.length || 0} incident${(confirmDelete?.ids?.length || 0) === 1 ? "" : "s"}?`
                                    : "Delete incident?"}
                            </DialogTitle>
                            <DialogDescription>
                                Attached files will also be permanently removed. This cannot be undone.
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => setConfirmDelete(null)}
                                disabled={deleting}
                                data-testid="incidents-delete-cancel"
                            >
                                Cancel
                            </Button>
                            <Button
                                className="bg-[color:var(--asl-crimson,#A8324A)] text-white hover:opacity-90"
                                onClick={runDelete}
                                disabled={deleting}
                                data-testid="incidents-delete-confirm"
                            >
                                {deleting ? "Deleting…" : "Delete permanently"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}

            {createOpen && (
                <CreateIncidentDialog
                    moduleId={moduleId}
                    open={createOpen}
                    onOpenChange={setCreateOpen}
                    onCreated={() => { setCreateOpen(false); load(); }}
                />
            )}
            {detailId && (
                <DetailDrawer
                    incidentId={detailId}
                    open={!!detailId}
                    onOpenChange={(v) => !v && setDetailId(null)}
                    onChanged={load}
                    user={user}
                />
            )}
        </div>
    );
}

// --- filter bar -------------------------------------------------------------
function FilterBar({ filters, setFilters, onClear }) {
    const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
    return (
        <div className="asl-card p-4 flex flex-wrap items-center gap-3" data-testid="incidents-filters">
            <div className="flex items-center gap-2">
                <Label className="text-xs asl-text-2">Status</Label>
                <Select value={filters.status} onValueChange={(v) => set("status", v)}>
                    <SelectTrigger className="w-44" data-testid="incidents-filter-status">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="any">Any status</SelectItem>
                        {STATUSES.map((s) => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div className="flex items-center gap-2">
                <Label className="text-xs asl-text-2">Severity</Label>
                <Select value={filters.severity} onValueChange={(v) => set("severity", v)}>
                    <SelectTrigger className="w-36" data-testid="incidents-filter-severity">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="any">Any severity</SelectItem>
                        {SEVERITIES.map((s) => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div className="flex items-center gap-2">
                <Label className="text-xs asl-text-2">Type</Label>
                <Select value={filters.incident_type} onValueChange={(v) => set("incident_type", v)}>
                    <SelectTrigger className="w-56" data-testid="incidents-filter-type">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="any">Any type</SelectItem>
                        {Object.entries(TYPE_LABELS).map(([v, l]) => (
                            <SelectItem key={v} value={v}>{l}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <label
                className="ml-2 inline-flex items-center gap-2 text-sm asl-text-1 cursor-pointer"
                data-testid="incidents-filter-redflag-label"
            >
                <Checkbox
                    checked={filters.red_flag_only}
                    onCheckedChange={(v) => set("red_flag_only", !!v)}
                    data-testid="incidents-filter-redflag"
                />
                Red flags only
            </label>
            {/* Iter70 — Clear filters resets every managed filter and drops
                the corresponding URL params. */}
            {onClear && (
                <Button
                    size="sm"
                    variant="outline"
                    onClick={onClear}
                    className="ml-auto"
                    data-testid="incidents-clear-filters"
                    title="Reset all filters to their defaults"
                >
                    Clear filters
                </Button>
            )}
        </div>
    );
}

// --- row --------------------------------------------------------------------
function IncidentRow({ it, onOpen, isAdmin, selected, onToggleSelect, onDeleteRow }) {
    const sev = SEVERITY_TONE[it.severity] || SEVERITY_TONE.low;
    const st = STATUS_TONE[it.status] || STATUS_TONE.open;
    return (
        <tr
            className="cursor-pointer hover:bg-black/[0.03] transition-colors"
            style={{
                borderTop: "1px solid var(--asl-divider, rgba(0,0,0,0.08))",
                borderLeft: it.red_flag ? "3px solid var(--asl-crimson, #A8324A)" : "3px solid transparent",
            }}
            onClick={onOpen}
            data-testid={`incidents-row-${it.id}`}
        >
            {isAdmin && (
                <td
                    className="px-4 py-3 w-10"
                    onClick={(e) => e.stopPropagation()}
                >
                    <Checkbox
                        checked={!!selected}
                        onCheckedChange={() => onToggleSelect?.()}
                        data-testid={`incidents-select-${it.id}`}
                        aria-label="Select incident"
                    />
                </td>
            )}
            <td className="px-4 py-3 whitespace-nowrap asl-text-1">{fmtDate(it.date_time)}</td>
            <td className="px-4 py-3">
                {it.red_flag ? (
                    <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide"
                        style={{
                            background: "rgba(168, 50, 74, 0.14)",
                            color: "var(--asl-crimson, #A8324A)",
                            border: "1px solid rgba(168, 50, 74, 0.28)",
                        }}
                        data-testid={`incident-red-flag-chip-${it.id}`}
                    >
                        Red flag
                    </span>
                ) : (
                    <span className="text-xs asl-text-3">—</span>
                )}
            </td>
            <td className="px-4 py-3 asl-text-1">{TYPE_LABELS[it.incident_type] || it.incident_type}</td>
            <td className="px-4 py-3">
                <span
                    className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase"
                    style={{ background: sev.bg, color: sev.fg }}
                    data-testid={`incident-severity-${it.id}`}
                >
                    {it.severity}
                </span>
            </td>
            <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1.5">
                    {(it.employees || []).slice(0, 4).map((e) => (
                        <span key={e.id} className="inline-flex items-center gap-1.5 text-xs asl-text-1">
                            <ModuleIdChip value={e.module_employee_id} size="sm" />
                            <span className="truncate max-w-[10rem]">{e.full_name}</span>
                        </span>
                    ))}
                    {(it.employees || []).length > 4 && (
                        <span className="text-xs asl-text-3">
                            +{(it.employees || []).length - 4} more
                        </span>
                    )}
                </div>
            </td>
            <td className="px-4 py-3 asl-text-1">
                <div className="truncate max-w-xs" title={it.location}>{it.location || "—"}</div>
            </td>
            <td className="px-4 py-3">
                <span
                    className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold"
                    style={{ background: st.bg, color: st.fg }}
                    data-testid={`incident-status-${it.id}`}
                >
                    {STATUSES.find((s) => s.value === it.status)?.label || it.status}
                </span>
            </td>
            <td className="px-4 py-3 asl-text-2 text-xs">
                {it.reporter_by_name || it.reporter_email || "—"}
            </td>
            {isAdmin && (
                <td
                    className="px-4 py-3 text-right w-16"
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        type="button"
                        onClick={onDeleteRow}
                        className="inline-flex items-center gap-1 text-xs font-semibold hover:underline"
                        style={{ color: "var(--asl-crimson, #A8324A)" }}
                        title="Delete incident"
                        data-testid={`incidents-delete-row-${it.id}`}
                    >
                        Delete
                    </button>
                </td>
            )}
        </tr>
    );
}

// --- create dialog ----------------------------------------------------------
export function CreateIncidentDialog({ moduleId, open, onOpenChange, onCreated, modulesForPicker, onPickModule }) {
    const [pickedModuleId, setPickedModuleId] = useState(moduleId || null);
    // Sync when parent updates moduleId (global directory changes selection).
    useEffect(() => { setPickedModuleId(moduleId || null); }, [moduleId]);
    const [state, setState] = useState({
        date_time: new Date().toISOString().slice(0, 16),  // "YYYY-MM-DDTHH:mm"
        location: "",
        project_id: "",
        incident_type: "other",
        severity: "medium",
        description: "",
        immediate_action_taken: "",
        witnesses_text: "",
        follow_up_required: false,
        follow_up_notes: "",
        manual_red_flag: false,
        linked_discipline_case_id: "",
    });
    const [empQuery, setEmpQuery] = useState("");
    const [empResults, setEmpResults] = useState([]);
    const [empSelected, setEmpSelected] = useState([]);
    const [projects, setProjects] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!pickedModuleId) return;
        (async () => {
            try {
                const [emp, proj] = await Promise.all([
                    api.get("/employees", { params: { module_id: pickedModuleId, page_size: 200 } }),
                    api.get("/projects", { params: { module_id: pickedModuleId, page_size: 200 } }),
                ]);
                setEmpResults(emp.data?.items || []);
                setProjects(proj.data?.items || proj.data || []);
            } catch (e) {
                toast.error(formatApiError(e));
            }
        })();
    }, [pickedModuleId]);

    const filteredEmployees = useMemo(() => {
        const q = empQuery.trim().toLowerCase();
        if (!q) return empResults.slice(0, 20);
        return empResults.filter((e) => {
            const name = (e.full_name || "").toLowerCase();
            const mid = (e.module_employee_id || "").toLowerCase();
            return name.includes(q) || mid.includes(q);
        }).slice(0, 20);
    }, [empResults, empQuery]);

    const toggleEmp = (id) => {
        setEmpSelected((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
    };

    const submit = async () => {
        if (!pickedModuleId) {
            toast.error("Pick a module first.");
            return;
        }
        if (!state.location.trim() || !state.description.trim()) {
            toast.error("Location and description are required.");
            return;
        }
        setSubmitting(true);
        try {
            const witnesses = state.witnesses_text
                .split("\n").map((w) => w.trim()).filter(Boolean);
            const payload = {
                module_id: pickedModuleId,
                date_time: new Date(state.date_time).toISOString(),
                location: state.location.trim(),
                project_id: state.project_id || null,
                employee_ids: empSelected,
                incident_type: state.incident_type,
                severity: state.severity,
                description: state.description.trim(),
                immediate_action_taken: state.immediate_action_taken.trim() || null,
                witnesses,
                follow_up_required: !!state.follow_up_required,
                follow_up_notes: state.follow_up_notes.trim() || null,
                manual_red_flag: !!state.manual_red_flag,
                linked_discipline_case_id: state.linked_discipline_case_id.trim() || null,
            };
            await api.post("/incidents", payload);
            toast.success("Incident filed.");
            onCreated?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="incidents-create-dialog">
                <DialogHeader>
                    <DialogTitle>File a new incident</DialogTitle>
                    <DialogDescription>
                        Red flags are computed automatically from severity + type; use the manual override
                        if the auto rules miss an edge case.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-2 gap-4 mt-2">
                    {modulesForPicker && modulesForPicker.length > 0 && (
                        <div className="col-span-2">
                            <Label>Module</Label>
                            <Select
                                value={pickedModuleId || ""}
                                onValueChange={(v) => { setPickedModuleId(v); onPickModule?.(v); }}
                            >
                                <SelectTrigger data-testid="incidents-form-module"><SelectValue placeholder="Pick a module" /></SelectTrigger>
                                <SelectContent>
                                    {modulesForPicker.map((m) => (
                                        <SelectItem key={m.id} value={m.id}>
                                            {m.code} — {m.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <div>
                        <Label htmlFor="i-date">Date &amp; time</Label>
                        <Input
                            id="i-date"
                            type="datetime-local"
                            value={state.date_time}
                            onChange={(e) => setState((s) => ({ ...s, date_time: e.target.value }))}
                            data-testid="incidents-form-date-time"
                        />
                    </div>
                    <div>
                        <Label htmlFor="i-loc">Location</Label>
                        <Input
                            id="i-loc"
                            value={state.location}
                            onChange={(e) => setState((s) => ({ ...s, location: e.target.value }))}
                            placeholder="Kismayo Airport — Gate 4"
                            data-testid="incidents-form-location"
                        />
                    </div>
                    <div>
                        <Label>Type</Label>
                        <Select
                            value={state.incident_type}
                            onValueChange={(v) => setState((s) => ({ ...s, incident_type: v }))}
                        >
                            <SelectTrigger data-testid="incidents-form-type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {Object.entries(TYPE_LABELS).map(([v, l]) => (
                                    <SelectItem key={v} value={v}>{l}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Severity</Label>
                        <Select
                            value={state.severity}
                            onValueChange={(v) => setState((s) => ({ ...s, severity: v }))}
                        >
                            <SelectTrigger data-testid="incidents-form-severity"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {SEVERITIES.map((s) => (
                                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="col-span-2">
                        <Label>Project (optional)</Label>
                        <Select
                            value={state.project_id || "none"}
                            onValueChange={(v) => setState((s) => ({ ...s, project_id: v === "none" ? "" : v }))}
                        >
                            <SelectTrigger data-testid="incidents-form-project"><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">— No project</SelectItem>
                                {projects.map((p) => (
                                    <SelectItem key={p.id} value={p.id}>
                                        {p.code ? `${p.code} · ${p.name}` : p.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="col-span-2">
                        <Label>Employees involved</Label>
                        <Input
                            placeholder="Search by name or Module ID…"
                            value={empQuery}
                            onChange={(e) => setEmpQuery(e.target.value)}
                            data-testid="incidents-form-employee-search"
                        />
                        {empSelected.length > 0 && (
                            <div
                                className="mt-2 flex flex-wrap gap-2"
                                data-testid="incidents-form-employee-chips"
                            >
                                {empSelected.map((id) => {
                                    const e = empResults.find((x) => x.id === id);
                                    return (
                                        <span
                                            key={id}
                                            className="inline-flex items-center gap-2 px-2 py-1 rounded bg-black/5 text-xs"
                                            data-testid={`incidents-form-employee-chip-${id}`}
                                        >
                                            <ModuleIdChip value={e?.module_employee_id} size="sm" />
                                            <span>{e?.full_name || id.slice(0, 8)}</span>
                                            <button
                                                type="button"
                                                onClick={() => toggleEmp(id)}
                                                className="asl-text-3 hover:asl-crimson-text"
                                                data-testid={`incidents-form-employee-remove-${id}`}
                                            >×</button>
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                        {empQuery && (
                            <div
                                className="mt-2 max-h-40 overflow-y-auto asl-card p-1"
                                data-testid="incidents-form-employee-results"
                            >
                                {filteredEmployees.map((e) => {
                                    const on = empSelected.includes(e.id);
                                    return (
                                        <div
                                            key={e.id}
                                            className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm ${on ? "bg-black/5" : "hover:bg-black/[0.03]"}`}
                                            onClick={() => toggleEmp(e.id)}
                                            data-testid={`incidents-form-employee-result-${e.id}`}
                                        >
                                            <Checkbox checked={on} />
                                            <ModuleIdChip value={e.module_employee_id} size="sm" />
                                            <span>{e.full_name}</span>
                                        </div>
                                    );
                                })}
                                {filteredEmployees.length === 0 && (
                                    <div className="px-2 py-1.5 text-sm asl-text-3">No matches.</div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="col-span-2">
                        <Label>Description</Label>
                        <Textarea
                            rows={4}
                            value={state.description}
                            onChange={(e) => setState((s) => ({ ...s, description: e.target.value }))}
                            placeholder="What happened, in the words of the reporter or witnesses…"
                            data-testid="incidents-form-description"
                        />
                    </div>
                    <div className="col-span-2">
                        <Label>Immediate action taken (optional)</Label>
                        <Textarea
                            rows={2}
                            value={state.immediate_action_taken}
                            onChange={(e) => setState((s) => ({ ...s, immediate_action_taken: e.target.value }))}
                            data-testid="incidents-form-immediate-action"
                        />
                    </div>
                    <div className="col-span-2">
                        <Label>Witnesses (one per line, optional)</Label>
                        <Textarea
                            rows={2}
                            value={state.witnesses_text}
                            onChange={(e) => setState((s) => ({ ...s, witnesses_text: e.target.value }))}
                            data-testid="incidents-form-witnesses"
                        />
                    </div>
                    <div className="col-span-2 flex items-start gap-3">
                        <Checkbox
                            checked={state.follow_up_required}
                            onCheckedChange={(v) => setState((s) => ({ ...s, follow_up_required: !!v }))}
                            data-testid="incidents-form-follow-up-required"
                        />
                        <div className="flex-1">
                            <Label className="cursor-pointer">Follow-up required</Label>
                            {state.follow_up_required && (
                                <Textarea
                                    className="mt-2"
                                    rows={2}
                                    value={state.follow_up_notes}
                                    onChange={(e) => setState((s) => ({ ...s, follow_up_notes: e.target.value }))}
                                    placeholder="What's the next step?"
                                    data-testid="incidents-form-follow-up-notes"
                                />
                            )}
                        </div>
                    </div>
                    <div className="col-span-2 flex items-center gap-3">
                        <Checkbox
                            checked={state.manual_red_flag}
                            onCheckedChange={(v) => setState((s) => ({ ...s, manual_red_flag: !!v }))}
                            data-testid="incidents-form-manual-red-flag"
                        />
                        <Label className="cursor-pointer">
                            Manually flag as red flag (overrides auto rules)
                        </Label>
                    </div>
                    <div className="col-span-2">
                        <Label>Linked discipline case (optional)</Label>
                        <Input
                            placeholder="Discipline record ID"
                            value={state.linked_discipline_case_id}
                            onChange={(e) => setState((s) => ({ ...s, linked_discipline_case_id: e.target.value }))}
                            data-testid="incidents-form-linked-discipline"
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        data-testid="incidents-form-cancel"
                    >Cancel</Button>
                    <Button
                        onClick={submit}
                        disabled={submitting}
                        className="asl-btn-primary"
                        data-testid="incidents-form-submit"
                    >
                        {submitting ? "Filing…" : "File incident"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// --- detail drawer ----------------------------------------------------------
export function DetailDrawer({ incidentId, open, onOpenChange, onChanged, user }) {
    const navigate = useNavigate();
    const [doc, setDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [statusValue, setStatusValue] = useState("open");
    const [statusNote, setStatusNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    const isManager = canManage(user);
    const isReporter = doc && user && doc.reporter_id === user.id;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(`/incidents/${incidentId}`);
            setDoc(res.data);
            setStatusValue(res.data?.status || "open");
        } catch (e) {
            toast.error(formatApiError(e));
            onOpenChange(false);
        } finally {
            setLoading(false);
        }
    }, [incidentId, onOpenChange]);

    useEffect(() => { load(); }, [load]);

    const saveStatus = async () => {
        setSaving(true);
        try {
            await api.patch(`/incidents/${incidentId}`, {
                status: statusValue,
                status_note: statusNote.trim() || null,
            });
            toast.success("Status updated.");
            setStatusNote("");
            await load();
            onChanged?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const uploadFile = async (fileList) => {
        if (!fileList || fileList.length === 0) return;
        setUploading(true);
        try {
            for (const file of Array.from(fileList)) {
                const form = new FormData();
                form.append("file", file);
                await api.post(
                    `/incidents/${incidentId}/attachments`,
                    form,
                    { headers: { "Content-Type": "multipart/form-data" } },
                );
            }
            toast.success("Attachment uploaded.");
            await load();
            onChanged?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setUploading(false);
        }
    };

    const removeAttachment = async (aid) => {
        try {
            await api.delete(`/incidents/${incidentId}/attachments/${aid}`);
            toast.success("Attachment removed.");
            await load();
            onChanged?.();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="w-[720px] sm:max-w-[720px] overflow-y-auto"
                data-testid="incidents-detail-drawer"
            >
                <SheetHeader>
                    <SheetTitle>Incident detail</SheetTitle>
                    <SheetDescription>
                        {doc?.red_flag && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 mt-2 rounded-full text-[11px] font-bold uppercase tracking-wide"
                                style={{
                                    background: "rgba(168, 50, 74, 0.14)",
                                    color: "var(--asl-crimson, #A8324A)",
                                    border: "1px solid rgba(168, 50, 74, 0.28)",
                                }}
                                data-testid="incident-detail-red-flag-chip"
                            >
                                Red flag
                            </span>
                        )}
                    </SheetDescription>
                </SheetHeader>

                {loading && <div className="p-4 asl-text-2">Loading…</div>}
                {!loading && doc && (
                    <div className="space-y-5 mt-4">
                        <section>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <Field label="Date &amp; time">{fmtDate(doc.date_time)}</Field>
                                <Field label="Type">{TYPE_LABELS[doc.incident_type] || doc.incident_type}</Field>
                                <Field label="Severity"><Badge variant="secondary">{doc.severity}</Badge></Field>
                                <Field label="Status"><Badge variant="secondary">
                                    {STATUSES.find((s) => s.value === doc.status)?.label || doc.status}
                                </Badge></Field>
                                <Field label="Location" full>{doc.location}</Field>
                                <Field label="Reporter" full>{doc.reporter_by_name || doc.reporter_email}</Field>
                                <Field label="Project" full>
                                    {doc.project_name || (doc.project_id ? doc.project_id.slice(0, 8) : "—")}
                                </Field>
                            </div>
                        </section>

                        <Separator />

                        <section>
                            <div className="asl-text-2 text-xs uppercase tracking-wide mb-2">Description</div>
                            <p className="text-sm asl-text-1 whitespace-pre-wrap" data-testid="incident-detail-description">
                                {doc.description}
                            </p>
                        </section>

                        {doc.immediate_action_taken && (
                            <section>
                                <div className="asl-text-2 text-xs uppercase tracking-wide mb-2">Immediate action taken</div>
                                <p className="text-sm asl-text-1 whitespace-pre-wrap">{doc.immediate_action_taken}</p>
                            </section>
                        )}

                        <section>
                            <div className="asl-text-2 text-xs uppercase tracking-wide mb-2">Employees involved</div>
                            {(doc.employees || []).length === 0 ? (
                                <div className="text-sm asl-text-3">—</div>
                            ) : (
                                <div className="flex flex-wrap gap-2">
                                    {(doc.employees || []).map((e) => (
                                        <span key={e.id} className="inline-flex items-center gap-2 px-2 py-1 rounded bg-black/5 text-xs">
                                            <ModuleIdChip value={e.module_employee_id} size="sm" />
                                            <span>{e.full_name}</span>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </section>

                        {(doc.witnesses || []).length > 0 && (
                            <section>
                                <div className="asl-text-2 text-xs uppercase tracking-wide mb-2">Witnesses</div>
                                <ul className="text-sm asl-text-1 list-disc pl-5 space-y-0.5">
                                    {doc.witnesses.map((w, i) => <li key={i}>{w}</li>)}
                                </ul>
                            </section>
                        )}

                        {doc.follow_up_required && (
                            <section>
                                <div className="asl-text-2 text-xs uppercase tracking-wide mb-2">Follow-up</div>
                                <p className="text-sm asl-text-1 whitespace-pre-wrap">
                                    {doc.follow_up_notes || "Required — no notes yet."}
                                </p>
                            </section>
                        )}

                        {doc.linked_discipline_case_id && (
                            <section>
                                <div className="asl-text-2 text-xs uppercase tracking-wide mb-2">Related discipline case</div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        // Discipline lives under `/m/{code}/discipline?open={id}`.
                                        const code = (doc.module_code || "").toLowerCase();
                                        if (code) navigate(`/m/${code}/discipline?open=${doc.linked_discipline_case_id}`);
                                    }}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded text-sm font-semibold hover:underline"
                                    style={{
                                        background: "rgba(27, 42, 86, 0.08)",
                                        color: "var(--asl-navy, #1B2A56)",
                                        border: "1px solid rgba(27, 42, 86, 0.18)",
                                    }}
                                    data-testid="incident-detail-linked-discipline-chip"
                                >
                                    <span className="mono text-[11px]">
                                        {doc.linked_discipline_case_id.slice(0, 8)}
                                    </span>
                                    <span>Open related discipline case →</span>
                                </button>
                            </section>
                        )}

                        <Separator />

                        <AttachmentsSection
                            attachments={doc.attachments || []}
                            onUpload={uploadFile}
                            onRemove={removeAttachment}
                            uploading={uploading}
                            canUpload={isManager || (isReporter && !["resolved", "closed"].includes(doc.status))}
                            canRemove={(a) => user?.role === ROLES.KAAFI_ADMIN
                                || (isReporter && !["resolved", "closed"].includes(doc.status))}
                            incidentId={incidentId}
                        />

                        <Separator />

                        <section>
                            <div className="asl-text-2 text-xs uppercase tracking-wide mb-2">Status history</div>
                            <ol className="text-sm asl-text-1 space-y-1.5" data-testid="incident-detail-status-history">
                                {(doc.status_history || []).map((h, i) => (
                                    <li key={i} className="flex items-start gap-3">
                                        <Badge variant="secondary">
                                            {STATUSES.find((s) => s.value === h.status)?.label || h.status}
                                        </Badge>
                                        <div className="flex-1">
                                            <div className="text-xs asl-text-3">
                                                {fmtDate(h.changed_at)} · {h.changed_by_email || h.changed_by || "system"}
                                            </div>
                                            {h.notes && <div className="text-sm asl-text-1 mt-0.5">{h.notes}</div>}
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </section>

                        {isManager && (
                            <section>
                                <div className="asl-text-2 text-xs uppercase tracking-wide mb-2">Change status</div>
                                <div className="grid grid-cols-2 gap-3">
                                    <Select value={statusValue} onValueChange={setStatusValue}>
                                        <SelectTrigger data-testid="incidents-status-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {STATUSES.map((s) => (
                                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Input
                                        placeholder="Note (optional)"
                                        value={statusNote}
                                        onChange={(e) => setStatusNote(e.target.value)}
                                        data-testid="incidents-status-note"
                                    />
                                </div>
                                <Button
                                    className="mt-3 asl-btn-primary"
                                    onClick={saveStatus}
                                    disabled={saving || statusValue === doc.status}
                                    data-testid="incidents-status-save"
                                >
                                    {saving ? "Saving…" : "Update status"}
                                </Button>
                            </section>
                        )}
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}

// --- attachments -----------------------------------------------------------
function AttachmentsSection({ attachments, onUpload, onRemove, uploading, canUpload, canRemove, incidentId }) {
    const inputRef = React.useRef(null);
    return (
        <section data-testid="incidents-attachments">
            <div className="flex items-center justify-between mb-2">
                <div className="asl-text-2 text-xs uppercase tracking-wide">Attachments</div>
                {canUpload && (
                    <>
                        <input
                            ref={inputRef}
                            type="file"
                            multiple
                            className="hidden"
                            onChange={(e) => onUpload(e.target.files)}
                            data-testid="incidents-attachments-input"
                        />
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={uploading}
                            onClick={() => inputRef.current?.click()}
                            data-testid="incidents-attachments-upload-btn"
                        >
                            {uploading ? "Uploading…" : "+ Upload"}
                        </Button>
                    </>
                )}
            </div>
            {attachments.length === 0 ? (
                <div className="text-sm asl-text-3">No attachments.</div>
            ) : (
                <ul className="space-y-1.5">
                    {attachments.map((a) => (
                        <li
                            key={a.id}
                            className="flex items-center justify-between gap-3 px-3 py-2 rounded bg-black/[0.03]"
                            data-testid={`incidents-attachments-file-${a.id}`}
                        >
                            <div className="flex-1 min-w-0">
                                <a
                                    href={`${API_BASE}/incidents/${incidentId}/attachments/${a.id}`}
                                    target="_blank"
                                    rel="noopener"
                                    className="text-sm asl-navy-text underline truncate block"
                                    data-testid={`incidents-attachments-download-${a.id}`}
                                >
                                    {a.filename}
                                </a>
                                <div className="text-xs asl-text-3">
                                    {a.mime_type} · {fmtBytes(a.size_bytes)}
                                </div>
                            </div>
                            {canRemove?.(a) && (
                                <button
                                    type="button"
                                    className="text-xs asl-crimson-text hover:underline"
                                    onClick={() => onRemove(a.id)}
                                    data-testid={`incidents-attachments-remove-${a.id}`}
                                >Remove</button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

function Field({ label, full, children }) {
    return (
        <div className={full ? "col-span-2" : ""}>
            <div className="asl-text-3 text-xs uppercase tracking-wide">{label}</div>
            <div className="asl-text-1 mt-0.5">{children}</div>
        </div>
    );
}
